"use client";
import React, { FC, useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";

import { ArrowDownIcon } from "@/components/assets/icons";

import { ShowWhen } from "@/components/common";
import { useOutsideClickDetector } from "@/hooks/useOutsideClickDetector";

import { useTheme } from "@/context/Theme.context";
import { InterfaceLanguage, Theme } from "@/enums";
import {
  getLanguagesList,
  initializeLanguage,
} from "@/utils/switchLanguageUtil";
import { LanguagesList } from "./languages-list";
import { useGetInterfaceLanguageRequest } from "@/api/useGetInterfaceLanguageRequest";

type TProps = {
  isExternalPage?: boolean;
};

const SwitchLanguage: FC<TProps> = ({ isExternalPage = false }) => {
  const [lang, setLang] = useState<InterfaceLanguage | undefined>(undefined);
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef(null);

  const theme = useTheme() as Theme;

  const {
    data: languageData,
    refetch,
    isLoading,
  } = useGetInterfaceLanguageRequest(!isExternalPage);

  const languagesList = getLanguagesList(theme as Theme);

  const selectedLang =
    languagesList.filter((e) => e.show).find((item) => item.name === lang) ||
    null;

  const SelectedLangIcon = selectedLang?.icon || null;

  const handleToggle = () => setIsOpened((prev) => !prev);

  useOutsideClickDetector(ref, () => {
    setIsOpened(false);
  });

  useLayoutEffect(() => {
    initializeLanguage(isLoading, theme, languageData, isExternalPage, setLang);
  }, [languageData, theme, isLoading, isExternalPage]);

  return (
    <div
      ref={ref}
      className={classNames(
        "relative flex h-[50px] w-[70px] items-center p-[11px] transition-all",
        {
          "rounded-tl-[25px] rounded-tr-[25px] bg-[#f5f5f5]": isOpened,
          "rounded-[25px] bg-white": !isOpened && !isExternalPage,
          "rounded-[25px] bg-[#f5f5f5]": !isOpened && isExternalPage,
        },
      )}
    >
      <div
        className="flex cursor-pointer items-center gap-1"
        onClick={handleToggle}
      >
        <ShowWhen condition={!!selectedLang}>
          <div className="flex items-center justify-center overflow-hidden">
            {SelectedLangIcon && (
              <SelectedLangIcon className="h-[28px] w-[28px]" />
            )}
          </div>
          <ArrowDownIcon className="cursor-pointer" />
        </ShowWhen>
      </div>
      <LanguagesList
        opened={isOpened}
        close={() => setIsOpened(false)}
        selectedLang={lang}
        languagesList={languagesList}
        refetch={refetch}
        isExternalPage={isExternalPage}
      />
    </div>
  );
};

export { SwitchLanguage };
